import { plural } from '../shared/wikiFormat';

/**
 * Verbatim prototype copy for the Wiki create modals (C2-modals-import §1).
 * One place so the components and the tests agree on every string.
 *
 * Deviation (shared with FE-foundation's `plural`): counts use the singular
 * for 1 ("1 block", "1 row") where the prototype printed "1 blocks".
 */

export const WIKI_TEMPLATE_NAMES = Object.freeze(['Blank', 'Policy', 'How-to', 'Playbook']);

/** Prototype `wkTemplateMd` (824,809) — the exact Markdown each template starts from. */
export const WIKI_TEMPLATE_MD = Object.freeze({
    Blank: '',
    Policy: '## Purpose\n\n\n\n## Who this applies to\n\n\n\n## The policy\n\n- \n\n## Exceptions\n\n',
    'How-to': '## Before you start\n\n\n\n## Steps\n\n1. \n\n## If something goes wrong\n\n',
    Playbook: '## When to use this\n\n\n\n## Plays\n\n- \n\n## What good looks like\n\n',
});

/** `templateMarkdown('How-to')` → the template body; unknown → Blank (""). */
export function templateMarkdown(name) {
    return Object.prototype.hasOwnProperty.call(WIKI_TEMPLATE_MD, name) ? WIKI_TEMPLATE_MD[name] : '';
}

/** Match an opts.template value case-insensitively ('how-to' → 'How-to'); null when unknown. */
export function normalizeTemplateName(value) {
    if (typeof value !== 'string') return null;
    const wanted = value.trim().toLowerCase();
    return WIKI_TEMPLATE_NAMES.find((name) => name.toLowerCase() === wanted) || null;
}

// Who can view / edit choices live in ../access/wikiAccessRules.js (#871).
export const WIKI_ANSWER_OPTIONS = Object.freeze(['Rewrite clearly', 'Quote exactly']);

export const WIKI_PAGE_MODES = Object.freeze([
    Object.freeze({ id: 'write', label: 'Write it here' }),
    Object.freeze({ id: 'import', label: 'Import a file' }),
]);

export const WIKI_MODAL_COPY = Object.freeze({
    titleCategory: 'New category',
    titleSubcategory: (parentName) => `New subcategory in ${parentName || ''}`.trimEnd(),
    titlePage: 'New page',
    titleImport: 'Import a page',
    close: 'Close',
    cancel: 'Cancel',
    create: 'Create',
    createAndWrite: 'Create and start writing',
    publishNow: 'Publish now',
    createDraft: 'Create draft and review',

    nameLabel: 'NAME',
    namePlaceholder: 'Give it a clear name',
    descLabel: 'DESCRIPTION',
    descPlaceholder: 'What belongs here? This also helps the AI route questions to it.',
    iconColorLabel: 'ICON AND COLOR',
    answerLabel: 'HOW ASK THE WIKI USES THESE PAGES',
    answerHint: Object.freeze({
        'Rewrite clearly': 'Answers are written in clear language from these pages. Numbers, names and rules stay exactly as written.',
        'Quote exactly': 'Answers quote this category word for word. Use it for legal, compliance and security text.',
    }),
    assistantTitle: 'Category assistant',
    assistantDesc: 'Adds an "Ask" button that answers only from this category. Answers still respect each page\'s access rules.',
    // #871: true for what each role is offered — admins can choose below,
    // editors can't change access.
    parentNote: (parentName) => `Inside ${parentName}. It uses ${parentName}'s access unless you choose otherwise below.`,
    parentNoteFixed: (parentName) => `Inside ${parentName}. It uses ${parentName}'s access.`,

    locationLabel: 'LOCATION',
    startFromLabel: 'START FROM',
    requiredTitle: 'Require acknowledgement',
    requiredDesc: 'Everyone who can view the page is asked to confirm they\'ve read it, and asked again when it changes.',

    pickerPlaceholder: 'Find a category or subcategory',
    pickerEmpty: 'No category matches. Create one from the Wiki sidebar.',
    pickerHint: '↑ ↓ to move · Enter to pick · Esc to close',
    // Real-build only (the prototype always had a location): no category the
    // caller can write to, or the list is still loading.
    pickerNoLocation: 'Choose a category',

    chooseTitle: 'Choose a file to import',
    chooseTypes: 'Markdown (.md, .markdown), plain text (.txt) or Word (.docx), up to 6 MB',
    sampleLead: 'No file handy?',
    sampleLink: 'Try a sample: refund-policy.md',
    importInfo: 'Headings become sections, and a top-level heading becomes the page title. Lists, numbered steps, checklists, tables, quotes, code and "> [!WARNING]" callouts keep their shape.',
    replace: 'Replace',
    pageTitleLabel: 'PAGE TITLE',
    previewLabel: 'PREVIEW',
    // Real-build only: a .docx is converted on the server before it can be previewed.
    reading: 'Reading the file…',
});

/** Settings, Move, Archive (#872): the category and page "⋯" menus and their modals. */
export const WIKI_MANAGE_COPY = Object.freeze({
    menuLabel: 'More actions',
    settings: 'Settings',
    move: 'Move…',
    archive: 'Archive',
    confirm: 'Confirm',
    save: 'Save',
    titleSettings: (name) => (name ? `Settings for ${name}` : 'Category settings'),
    // The switch is real: a scoped Ask is refused while it's off (wiki_category_assistant_off).
    assistantDescSettings: 'Adds an "Ask" button that answers only from this category. When it\'s off, Ask can\'t be used from this category, its subcategories or its pages.',
    counting: 'Counting what goes with it…',
    archiveCategoryTitle: (name) => `Archive ${name}?`,
    archiveCategoryBody: ({ subcategories = 0, pages = 0 } = {}) => {
        const parts = [];
        if (subcategories) parts.push(plural(subcategories, 'subcategory', 'subcategories'));
        if (pages) parts.push(plural(pages, 'page'));
        if (parts.length === 0) return 'Nothing else is archived with it. A Wiki admin can restore it from Archived.';
        return `This also archives ${parts.join(' and ')}. Restoring it brings back exactly these.`;
    },
    archivePageTitle: (title) => `Archive ${title}?`,
    archivePageBody: 'It disappears from lists, search, Ask and To acknowledge until it\'s restored. Editors and admins can restore it from Archived.',
    moveTitle: (title) => `Move ${title}`,
    moveLabel: 'NEW LOCATION',
    moveSubmit: 'Move page',
    moveNone: 'There\'s no other category you can edit. Ask a Wiki admin for edit access.',
    // A page's own access rules (page-scope ACL rows) travel with it and still apply.
    moveNote: 'Who can see this page follows its new location\'s access, unless the page has its own access rules.',
    archiveCategoryErrors: Object.freeze({
        wiki_category_archived: 'This category is already archived. Refresh to see it.',
        wiki_category_not_found: 'This category no longer exists. Refresh the Wiki.',
    }),
    archivePageErrors: Object.freeze({
        wiki_page_archived: 'This page is already archived.',
        wiki_page_not_found: 'This page no longer exists. Refresh the Wiki.',
    }),
});

export const WIKI_IMPORT_ERRORS = Object.freeze({
    type: 'That file type isn\'t supported. Use .md, .markdown, .txt or .docx.',
    size: 'That file is over 6 MB. Split it into smaller pages and import them one by one.',
    // Real-build only: FileReader failed (no prototype copy).
    unreadable: 'That file couldn\'t be read. Try again, or choose another file.',
});

export const WIKI_IMPORT_WARNINGS = Object.freeze({
    noHeading: 'No top-level heading found, so the file name is used as the title.',
    html: 'The file contains HTML. It is kept as text, not run.',
});

export const WIKI_MODAL_TOASTS = Object.freeze({
    categoryCreated: () => 'Category created. It also became an option in the AI Brain\'s "Which category?" check.',
    subcategoryCreated: (parentName) => `Subcategory created in ${parentName}. People who can see ${parentName} can see it.`,
    importPublished: (blocks) => `Imported and published ${plural(blocks, 'block')}. The AI can use it in about a minute.`,
    importDraft: (blocks) => `Imported ${plural(blocks, 'block')} as a draft. Edit it right here, or switch to Markdown to edit the file as written.`,
    // #872
    categorySaved: () => 'Settings saved.',
    categoryArchived: (name) => `${name} archived. Wiki admins can restore it from Archived.`,
    categoryRestored: (name) => `${name} restored.`,
    pageMoved: (where) => `Page moved to ${where}.`,
    pageArchived: () => 'Page archived. You can restore it from Archived.',
    pageRestored: (title) => `${title} restored.`,
});
